import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IVAT } from "../../../interfaces/IVat";
import DynamicForm from "../../../yoello-lib/modules/dynamicForms/DynamicForms";
import ISubCategory from "../categories/ISubCategory";
import IMenuItemBody from "../../../interfaces/IMenuItemBody";
import { menuItemAPICall } from "./ItemsContainer";
import { formatCurrencyNoIcon } from "../../../yoello-lib/modules/localisation/useCurrency";
import { assignExtraGroups, IMenuItemSubmitObject, publishMenuItem } from "../../../services/menuItemController";
import EColourPanel from "../../../interfaces/EColourPanel";
import { IReduxAppState } from "../../../reducers";
import { useTranslation } from "react-i18next";
import { useSnackbarMessages } from "../../../yoello-lib/modules/components/snackbar/SnackbarContext";
import ListLoading from "../../util/Loading";
import { getModifyProductsFormRows } from "../../../formSchemas/modifyProduct.form";
import useTask from "../../../hooks/useTask";
import { useAPIV2 } from "../../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../../yoello-lib/modules/API2/APIDefs";
import { ITaxes } from "../../../interfaces/ITaxes";
import { HTTPMethods } from "../../../yoello-lib/modules/API/API";
import { IExtraGroup } from "../../../interfaces/IExtraGroup";
import { IPreference } from "../preferences/PreferenceForm";
import { FormPermissions } from "../../../Permissions";
import { setCanCloseDialog } from "../../../actions/dialog";
import { calculateAdditiveTaxes, calculateInclusiveTaxes, getTaxesType } from "../../../yoello-lib/modules/utils/TaxController";
import { ITaxType } from "../../../yoello-lib/modules/interfaces/ITaxes";
import { IMenuData } from "../../manageMenu/types";
import { IDialogRouteChildren } from "../../dialogRoutes/DialogRoute";
import { useChangedDialog } from "../../../hooks/useChangedDialog";
import { productValidationResolver } from "../../../formValidation/modifyProduct.validation";
import useCheckPermissions from "../../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../../const/permissions.const";
import { isObjectEmpty } from "../../../utils/objectCheck";
import { useAuthenticationContext } from "../../../yoello-lib/modules/auth/AuthenticationContext";
import { FormLoadingWrapper } from "../../util/FormLoadingWrapper";

export interface IProductModifyProps extends IDialogRouteChildren {
    item: IMenuItemBody;
    subcategories: ISubCategory[];
    vat: IVAT[];
    menuItemAPICall: menuItemAPICall;
    venueId: string;
    switchCategory: (id: string) => void;
    tags: IItemTags[],
    removeAddToExtraGroup?: boolean,
    manageMenuAPI: any;
    orphanedMenuItemsAPI?: any;
    id: string;
    removeSelectedItem?: (id: string) => void;

}

export function ProductModifyDialog(props: IProductModifyProps) {
    const {
        item,
        subcategories,
        menuItemAPICall,
        venueId,
        onClose,
        switchCategory,
        tags,
        removeAddToExtraGroup,
        orphanedMenuItemsAPI,
        id,
        isNewItem,
        removeSelectedItem,
        onDialogClose
    } = props;
    const isSubCats = Array.isArray(subcategories) && subcategories.length > 0;
    const isEposNow = useSelector((state: IReduxAppState) => state.isEposNow);
    const subscription = useSelector((state: IReduxAppState) => state.subscription);
    const selectedVenueId = useSelector((state: IReduxAppState) => state.selectedVenueId);
    const venueData = useSelector((state: IReduxAppState) => state.venue[selectedVenueId]?.venueData);
    const venueHasMigratedToMenus = useSelector((state: IReduxAppState) => state.venue[selectedVenueId]?.venueSettings?.on_menu_setup);
    const { hasFormChanged } = useChangedDialog();
    const { retrieveAccessToken } = useAuthenticationContext();

    const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.MENU_MANAGEMENT);

    /**Form data values */
    const { apiData: taxesData } = useAPIV2<ITaxes[]>(APIExtensions.taxes,
        { yoelloAPIParams: { venue_id: venueId, size: 200 }, isPaginated: true });
    const { apiData: extrasData } = useAPIV2<IExtraGroup[]>(APIExtensions.extraGroups,
        { yoelloAPIParams: { venue_id: venueId, size: 200 }, isPaginated: true });
    const { apiData: preferenceData } = useAPIV2<IPreference[]>(APIExtensions.menuItemPreferences,
        { yoelloAPIParams: { venue_id: venueId, size: 200 }, isPaginated: true });
    const { apiData: menusList, isLoading: menusLoading } = useAPIV2<IMenuData[]>(APIExtensions.menus,
        { yoelloAPIParams: { venue_id: venueId, size: 200, include_disabled: true }, isPaginated: true });
    const { apiData: subcategoriesData, isLoading: isSubLoading } = useAPIV2<ISubCategory[]>(APIExtensions.menuItemSubcategories,
        { yoelloAPIParams: { venue_id: venueId, size: 200, include_disabled: true }, isPaginated: true, onMountDisabled: isSubCats ? true : false });
    /**API call for connecting menu item relationships */
    const { callAPI: menuItemRelationshipsCall } = useAPIV2<any>(APIExtensions.menusItems,
        { onMountDisabled: true });
    /**Menus  */
    const { callAPI: manageMenuAPICall } = useAPIV2(APIExtensions.menus, {
        onMountDisabled: true,
        yoelloAPIParams: { venue_id: venueId, size: 200, include_disabled: true, },
        isPaginated: true,
    });
    const { callAPI: extraTag } = useAPIV2<any>(APIExtensions.extraGroups, { 
        query:venueId, onMountDisabled: true });
    
    const { t } = useTranslation();
    const { increaseDoneTasks } = useTask();
    const [isLoading, setIsLoading] = useState(false);
    const { sendSnackbarMessage } = useSnackbarMessages()!;
    const isSquare = venueData?.payment_processor === 'SQUARE';
    const [formDataState, setFormData] = useState(createFormData(item, isSquare, tags ?? [], t));
    const [itemMenuList, setItemMenuList] = useState<IMenuData[]>([]);
    const [itemExtras, setItemExtras] = useState<IExtraGroup[]>([]);
    const dispatcher = useDispatch();
    const { callAPI: menuItemsExtrasGroups } = useAPIV2(
        APIExtensions.menuItemsExtrasGroups,
        {
            onMountDisabled: true,
        }
    );

    useEffect(() => {
        if (item?.id || !isNewItem) {

            //@ts-ignore
            async function getMenuData() {
                setIsLoading(true);
                const menuItem: any = await menuItemAPICall(HTTPMethods.GET, {
                    getChildrenData: true,
                    query: item?.id ?? id
                })
                if (menuItem.isSuccessful) {
                    setItemMenuList(menuItem.data.menus);
                    setItemExtras(menuItem.data.extra_groups);
                    setFormData(createFormData(menuItem.data, isSquare, tags, t));
                    setIsLoading(false);
                }
            }
            getMenuData();
        }
        //eslint-disable-next-line
    }, []);
    function onDelete() {
        if (orphanedMenuItemsAPI) {
            orphanedMenuItemsAPI(HTTPMethods.GET)
        }
    }

    async function onSubmit(formData: Partial<IMenuItemSubmitObject>) {
        const extraGroups = formData.extra_groups;
        const selectedMenus = formData.menus;
        const oldExtras = itemExtras;
        formData.menus = undefined;
        formData.extraGroups = undefined;
        console.log({ formData });
        setIsLoading(true);
        dispatcher(setCanCloseDialog(false));
        const submitObject: any =
        {
            id: item?.id,
            ...formData,
            venue_id: venueId
        };

        if (!formData.tags) {
            setIsLoading(false);
            dispatcher(setCanCloseDialog(true));
            return;
        }
        if (Array.isArray(formData.tags)) {
            submitObject.tags = formData.tags.map((tag: any) => tag.id);
        }
        setFormData(submitObject);
        try {
            const isSuccessPublishMenuItem = await publishMenuItem(submitObject, venueId,
                menuItemAPICall, extraTag, menuItemRelationshipsCall, retrieveAccessToken);
            if (!isSuccessPublishMenuItem) {
                throw new Error("Menu item failed");
            }

            let isSuccessMenuItemsExtrasGroups;
            let isSuccessMenusAttachment;
            if (extraGroups || oldExtras) {
                const selectedGroups: IExtraGroup[] = !formData.is_extra ? [] : extraGroups as IExtraGroup[];
                const isMainMenuItem = isObjectEmpty(formData.pricing_options_extras);
                //@ts-ignore
                isSuccessMenuItemsExtrasGroups = await assignExtraGroups(isSuccessPublishMenuItem, oldExtras ?? [], selectedGroups ?? [], menuItemsExtrasGroups);
                if (isMainMenuItem && removeSelectedItem) {
                    removeSelectedItem(isSuccessPublishMenuItem);
                }

            } else {
                isSuccessMenuItemsExtrasGroups = true
            }
            if (Array.isArray(selectedMenus)) {
                const removes = itemMenuList?.filter(item => !selectedMenus.includes(item))
                const appends = selectedMenus.filter(item => !itemMenuList?.includes(item))
                let bodyArray: any = [];
                if (removes) {
                    removes.forEach(item => {
                        bodyArray.push({
                            "menu_id": item.id,
                            "menu_items": {
                                "append": [],
                                "remove": [formData.id ?? isSuccessPublishMenuItem]
                            }
                        })
                    })
                }
                if (appends) {
                    appends.forEach(item => {
                        bodyArray.push({
                            "menu_id": item.id,
                            "menu_items": {
                                "append": [formData.id ?? isSuccessPublishMenuItem],
                                "remove": []
                            }
                        })
                    })
                }
                isSuccessMenusAttachment = await manageMenuAPICall(HTTPMethods.PATCH, {
                    body: bodyArray
                })
            } else {
                isSuccessMenusAttachment = true;
            }

            if (isSuccessPublishMenuItem && isSuccessMenuItemsExtrasGroups && isSuccessMenusAttachment) {
                if (submitObject?.id) {
                    sendSnackbarMessage(t("menu.products.modifiy.successmsgUpdate"), 'success');
                } else {
                    sendSnackbarMessage(t("menu.products.modifiy.successmsgCreate"), 'success');
                }
                increaseDoneTasks();
                if (switchCategory) {
                    switchCategory(formData.subcategory_id!);
                }
                if (orphanedMenuItemsAPI) {
                    orphanedMenuItemsAPI(HTTPMethods.GET)
                }
                if (onDialogClose) {
                    onDialogClose();
                } else {
                    onClose();
                }

            } else {
                sendSnackbarMessage(t("menu.products.modifiy.apierrormsg"), 'error');
            }
        } catch (error) {
            sendSnackbarMessage(t("snackbar.permissions.failure.menu_management"), "error");
        } finally {
            setIsLoading(false);
            dispatcher(setCanCloseDialog(true));
        }
    }
    const allergensList = useMemo(() => {
        return tags?.map((item: IItemTags) => {
            return {
                ...item,
                name_public: t(`allergens.${item.name_public}`)
            };
        }) || []
        //eslint-disable-next-line
    }, [tags])

    return isLoading || menusLoading || (!isSubCats && isSubLoading) ? <FormLoadingWrapper> <ListLoading /> </FormLoadingWrapper>  : <DynamicForm formData={{ ...formDataState }} formKeyName="mysettings"
        useFormOptions={
            {
                resolver: productValidationResolver,
                reValidateMode: "onChange",
                mode: "onChange"
            }
        }
        disabledFields={FormPermissions["productdialog"]}
        subscriptionKey={subscription}
        formRows={getModifyProductsFormRows({ ...props, t }, taxesData as any, isSubCats ? subcategories : subcategoriesData as ISubCategory[], isEposNow, extrasData as any, preferenceData as any, allergensList, venueHasMigratedToMenus ? menusList as IMenuData[] : undefined, removeAddToExtraGroup, onDelete , onFailedInteraction)}
        onHandleSubmit={onSubmit}
        isDialog={true}
        disableAll={!hasPermission}
        disableAllInteraction={onFailedInteraction}
        formButtons={{
            leftButton: {
                type: "button", label: t('forms.buttons.cancel'), color: EColourPanel.DEFAULT, fullWidth: false, onClick: onClose
            },
            rightButton: {
                type: "submit", label: t('forms.buttons.submit'), color: EColourPanel.PRIMARY, fullWidth: false, disabled: isLoading || !hasPermission
            }
        }}
        hasDataChanged={hasFormChanged}
    />;
}
function createFormData(formObject: any, hideExclusive: boolean, tags: IItemTags[], t: any) {
    let pricing_options: any = {};

    let extra_pricing_options: any = {};
    const taxType = getTaxesType(formObject?.item_taxes);

    const taxes = formObject?.item_taxes;
    const abv = formObject?.abv ? formObject?.abv / 10 : undefined;
    if (taxType === ITaxType.INCLUSIVE) {
        if (formObject?.pricing_options) {
            for (const priceOps in formObject.pricing_options) {
                const data = formObject.pricing_options[priceOps]
                const price = data.option_price * 0.01;
                const taxAmountPo = calculateInclusiveTaxes(taxes!, price);
                const option_price_exclusive = +(price - taxAmountPo).toFixed(2);
                pricing_options[priceOps] = {
                    ...data,
                    hide_exclusive: hideExclusive,
                    option_price: +formatCurrencyNoIcon(data.option_price as number),
                    option_price_exclusive: option_price_exclusive
                };
            }
        }
        if (formObject?.pricing_options_extras) {
            for (const priceOps in formObject.pricing_options_extras) {
                const data = formObject.pricing_options_extras[priceOps];
                const price = data.option_price * 0.01;
                const taxAmountPo = calculateInclusiveTaxes(taxes!, price);
                const option_price_exclusive = +(price - taxAmountPo).toFixed(2)
                extra_pricing_options[priceOps] = {
                    ...data, hide_exclusive: hideExclusive,
                    option_price: +formatCurrencyNoIcon(data.option_price as number),
                    option_price_exclusive
                };
            }
        }
    } else {
        if (formObject?.pricing_options) {
            for (const priceOps in formObject.pricing_options) {
                const data = formObject.pricing_options[priceOps];
                const price = calculateAdditiveTaxes(taxes, data.option_price) + data.option_price;
                pricing_options[priceOps] = {
                    ...data,
                    option_price: +formatCurrencyNoIcon(price),
                    option_price_exclusive: +formatCurrencyNoIcon(data.option_price)
                };
            }
        }
        if (formObject?.pricing_options_extras) {
            for (const priceOps in formObject.pricing_options_extras) {
                const data = formObject.pricing_options_extras[priceOps];
                const price = calculateAdditiveTaxes(taxes, data.option_price) + data.option_price;
                extra_pricing_options[priceOps] = {
                    ...data,
                    hide_exclusive: hideExclusive,
                    option_price: +formatCurrencyNoIcon(price),
                    option_price_exclusive: +formatCurrencyNoIcon(data.option_price)
                };
            }
        }
    }

    let tagsArray = [];
    if (tags
        && Array.isArray(tags)
        && Array.isArray(formObject?.tags)) {

        for (const tag of formObject.tags) {
            const index = tags.findIndex((t) => t.id === tag);
            tagsArray.push({ ...tags[index], name_public: t(`allergens.${tags[index]?.name_public}`) });
        }
    }
    const formData = {
        collection_enabled: true,
        delivery_enabled: true,
        table_service_enabled: true,
        // name_public: "",
        ...formObject,
        pricing_options,
        pricing_options_extras: extra_pricing_options,
        abv,
        tags: tagsArray,
        menus: formObject?.menus ?? [],

    };
    return formData;
}
export function createPricingOption(): any {
    return {
        option_name: "",
        option_price: 0,
        option_discount: 0,
        option_price_exclusive: 0
    };
};
